import React, { useState, useRef } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import styled from '@emotion/styled';
import NiftiFileReader from './NiftiFileReader';

const PageContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background: #f5f5f5;
`;

const UploadContainer = styled(Paper)`
  padding: 40px;
  margin: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  border: 2px dashed #c0c0c0;
  border-radius: 12px;
  background: #ffffff;
  transition: all 0.3s ease;

  &:hover {
    border-color: #f04a23;
    background: #fafafa;
  }

  &.drag-over {
    border-color: #f04a23;
    background: #fff5f2;
  }
`;

const UploadIcon = styled(Box)`
  font-size: 64px;
  color: #f04a23;
  margin-bottom: 16px;
`;

const UploadTitle = styled(Typography)`
  color: #333;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const UploadText = styled(Typography)`
  font-size: 16px;
  color: #666;
  text-align: center;
  max-width: 500px;
`;

const FileInput = styled.input`
  display: none;
`;

const UploadButton = styled(Button)`
  background: linear-gradient(to right, #f04a23, #ed8623a1);
  color: white;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 600;
  text-transform: none;
  border-radius: 8px;
  &:hover {
    background: linear-gradient(to right, #d43d1a, #d67219);
  }
`;

const FileInfo = styled(Box)`
  padding: 16px;
  margin: 20px;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const FileName = styled(Typography)`
  font-size: 16px;
  font-weight: 500;
  color: #333;
`;

const RemoveButton = styled(Button)`
  color: #f04a23;
  text-transform: none;
  &:hover {
    background: rgba(240, 74, 35, 0.1);
  }
`;

const ErrorMessage = styled(Box)`
  padding: 16px;
  margin: 20px;
  background: #ffebee;
  border-radius: 8px;
  border-left: 4px solid #f44336;
`;

const ErrorText = styled(Typography)`
  color: #c62828;
  font-size: 14px;
`;

function NiftiFileUploadPage() {
  const [niftiFile, setNiftiFile] = useState<ArrayBuffer | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    setError('');
    setFileName('');

    // Validate file type
    const validExtensions = ['.nii', '.nii.gz', '.hdr', '.img'];
    // const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))) {
      setError('Please select a valid NIFTI file (.nii, .nii.gz, .hdr, .img)');
      return;
    }

    // Read file as ArrayBuffer
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        if (arrayBuffer) {
          setNiftiFile(arrayBuffer);
          setFileName(file.name);
          setError('');
        }
      } catch (err) {
        setError('Error reading file. Please try again.');
        console.error('Error reading file:', err);
      }
    };

    reader.onerror = () => {
      setError('Error reading file. Please try again.');
    };

    reader.readAsArrayBuffer(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleRemoveFile = () => {
    setNiftiFile(null);
    setFileName('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <PageContainer>
      {!niftiFile ? (
        <>
          <UploadContainer
            className={isDragging ? 'drag-over' : ''}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            elevation={2}
          >
            <UploadIcon>
              <CloudUploadIcon sx={{ fontSize: 64 }} />
            </UploadIcon>
            <UploadTitle>Upload NIFTI File</UploadTitle>
            <UploadText>
              Drag and drop your NIFTI file here, or click the button below to browse and select a file.
              Supported formats: .nii, .nii.gz, .hdr, .img
            </UploadText>
            <FileInput
              ref={fileInputRef}
              type="file"
              // accept=".nii,.nii.gz,.hdr,.img"
              onChange={handleFileInputChange}
            />
            <UploadButton
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadClick}
            >
              Browse Files
            </UploadButton>
          </UploadContainer>

          {error && (
            <ErrorMessage>
              <ErrorText>{error}</ErrorText>
            </ErrorMessage>
          )}
        </>
      ) : (
        <>
          <FileInfo>
            <FileName>Loaded: {fileName}</FileName>
            <RemoveButton onClick={handleRemoveFile}>
              Remove File
            </RemoveButton>
          </FileInfo>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <NiftiFileReader 
              initialNiftiData={niftiFile}
              labelsFromContext={[]}
            />
          </Box>
        </>
      )}
    </PageContainer>
  );
}

export default NiftiFileUploadPage;

